import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Loader2, Check, Sparkles, GitBranch } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Modulo72MapaPatrones() {
    const [disparador, setDisparador] = useState('');
    const [respuesta, setRespuesta] = useState('');
    const [resultadoInmediato, setResultadoInmediato] = useState('');
    const [resultadoPosterior, setResultadoPosterior] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');
    const [patronDominante, setPatronDominante] = useState('');
    const [recurrencia, setRecurrencia] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/mapa_patrones`, { withCredentials: true });
                if (data.data) {
                    if (data.data.disparador) setDisparador(data.data.disparador);
                    if (data.data.respuesta) setRespuesta(data.data.respuesta);
                    if (data.data.resultadoInmediato) setResultadoInmediato(data.data.resultadoInmediato);
                    if (data.data.resultadoPosterior) setResultadoPosterior(data.data.resultadoPosterior);
                }
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch {} finally { setLoadingDraft(false); }
        })();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try { await axios.post(`${API_URL}/api/user/draft/save`, { module_id: 'mapa_patrones', data: { disparador, respuesta, resultadoInmediato, resultadoPosterior } }, { withCredentials: true }); } catch {} finally { setSavingDraft(false); }
    }, [disparador, respuesta, resultadoInmediato, resultadoPosterior]);

    useEffect(() => { if (loadingDraft || readOnly) return; const t = setTimeout(saveDraft, 2000); return () => clearTimeout(t); }, [disparador, respuesta, resultadoInmediato, resultadoPosterior, saveDraft, loadingDraft, readOnly]);

    const canSubmit = !readOnly && (disparador.trim().length > 0 && respuesta.trim().length > 0 && resultadoInmediato.trim().length > 0 && resultadoPosterior.trim().length > 0);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/user/modules/mapa-patrones/submit`, {
                disparador, respuesta, resultado_inmediato: resultadoInmediato, resultado_posterior: resultadoPosterior
            }, { withCredentials: true });
            setAiInsight(data.ai_insight);
            setPatronDominante(data.patron_dominante);
            setRecurrencia(data.recurrencia);
            setSubmitted(true);
            toast.success('Mapa de patrones completado');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); }
        finally { setLoading(false); }
    };

    if (loadingDraft) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <div className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl"><CardContent className="pt-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#E8B27A] to-[#D4A06A] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"><GitBranch className="h-10 w-10 text-white" /></div>
                    <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2" data-testid="mapa-patrones-success-title">Patrón identificado</h2>
                    <p className="text-[#E8B27A] font-medium mb-4">Tu secuencia conductual es visible</p>
                    {patronDominante && <div className="bg-[#E8B27A]/10 p-3 rounded-xl text-sm text-[#1F2A30] mb-3"><span className="font-semibold text-[#E8B27A]">Patrón dominante:</span> {patronDominante}</div>}
                    {recurrencia && <div className="inline-block px-4 py-1.5 bg-[#5FAF9D]/10 text-[#5FAF9D] rounded-full text-sm font-medium mb-4">Recurrencia: {recurrencia}</div>}
                    {aiInsight && <div className="bg-[#F5F7F8] border-l-4 border-[#E8B27A] p-4 rounded-r-xl text-left mb-6"><div className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-[#E8B27A] shrink-0 mt-0.5" /><p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p></div></div>}
                    <div className="mb-6"><ModuleFeedback moduleId="mapa_patrones" chapter={7} /></div>
                    <Link to="/usuario"><Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl" data-testid="continue-after-mapa-patrones">Continuar</Button></Link>
                </CardContent></Card>
            </div><SENDAFooter /></div>
    );

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <Link to="/usuario"><Button variant="ghost" size="icon" className="text-[#3E5F6E]"><ArrowLeft className="h-5 w-5" /></Button></Link>
                            <div>
                                <div className="flex items-center gap-2"><span className="text-xs font-semibold text-[#7A9BA8] uppercase tracking-wide">Cap. 7</span><span className="text-xs text-[#7A8A93]">7.2</span></div>
                                <h1 className="text-base sm:text-lg font-semibold text-[#1F2A30] font-['Outfit']">Mapa de patrones</h1>
                            </div>
                        </div>
                        {savingDraft && <Loader2 className="h-3 w-3 animate-spin text-[#7A8A93]" />}
                    </div>
                </div>
            </header>
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 flex-1">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="bg-white border border-[#DCE3E8] rounded-2xl sm:rounded-3xl shadow-sm overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-[#E8B27A] to-[#D4A06A] p-4 sm:p-6 text-white">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center"><GitBranch className="h-5 w-5 sm:h-6 sm:w-6 text-white" /></div>
                            <div><CardTitle className="text-white text-base sm:text-lg">Identifica tu secuencia</CardTitle><p className="text-xs sm:text-sm text-white/70">Disparador → Respuesta → Resultado inmediato → Resultado posterior</p></div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-4 sm:p-6 space-y-5">
                        <div className="space-y-2 p-4 bg-[#E8B27A]/5 rounded-xl border border-[#E8B27A]/20">
                            <Label className="text-sm font-semibold text-[#E8B27A]">Disparador</Label>
                            <p className="text-xs text-[#7A8A93]">¿Qué situación o señal activa tu respuesta habitual?</p>
                            <Textarea value={disparador} onChange={e => setDisparador(e.target.value)} placeholder="Ej: Cuando abro el correo y veo un mensaje del jefe pidiendo avances..." className="border-[#E8B27A]/20 bg-white rounded-xl min-h-[60px]" data-testid="input-disparador-patron" />
                        </div>
                        <div className="space-y-2 p-4 bg-[#3E5F6E]/5 rounded-xl border border-[#3E5F6E]/20">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">Respuesta habitual</Label>
                            <p className="text-xs text-[#7A8A93]">¿Qué haces automáticamente?</p>
                            <Textarea value={respuesta} onChange={e => setRespuesta(e.target.value)} placeholder="Ej: Cierro el correo, abro redes sociales y pospongo la respuesta..." className="border-[#3E5F6E]/20 bg-white rounded-xl min-h-[60px]" data-testid="input-respuesta-patron" />
                        </div>
                        <div className="space-y-2 p-4 bg-[#5FAF9D]/5 rounded-xl border border-[#5FAF9D]/20">
                            <Label className="text-sm font-semibold text-[#5FAF9D]">Resultado inmediato</Label>
                            <p className="text-xs text-[#7A8A93]">¿Qué sientes o consigues en el momento?</p>
                            <Textarea value={resultadoInmediato} onChange={e => setResultadoInmediato(e.target.value)} placeholder="Ej: Alivio momentáneo, la ansiedad baja temporalmente..." className="border-[#5FAF9D]/20 bg-white rounded-xl min-h-[60px]" data-testid="input-resultado-inmediato" />
                        </div>
                        <div className="space-y-2 p-4 bg-[#7A9BA8]/5 rounded-xl border border-[#7A9BA8]/20">
                            <Label className="text-sm font-semibold text-[#7A9BA8]">Resultado posterior</Label>
                            <p className="text-xs text-[#7A8A93]">¿Qué pasa horas o días después?</p>
                            <Textarea value={resultadoPosterior} onChange={e => setResultadoPosterior(e.target.value)} placeholder="Ej: Acumulación de pendientes, más ansiedad, autocrítica..." className="border-[#7A9BA8]/20 bg-white rounded-xl min-h-[60px]" data-testid="input-resultado-posterior" />
                        </div>
                        <div className="flex justify-end pt-4 border-t border-[#DCE3E8]">
                            <Button onClick={handleSubmit} disabled={!canSubmit || loading} className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-6 sm:px-8 py-3 rounded-xl shadow-md" data-testid="submit-mapa-patrones">
                                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Procesando...</> : <><Check className="h-4 w-4 mr-2" />Identificar patrón</>}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
